import { useEffect, useMemo, useState, MouseEvent, useRef } from 'react';
import dayjs from 'dayjs';
import {
  useDeleteLetterStore,
  useLetterContentStore,
  useLettersStore,
  useArchiveLetterStore,
  useViewLetterStore,
  useLettersCountStore,
} from '@lib/store';
import { useDebounce } from '@shared/hooks';
import { cn } from '@shared/utils';
import type { GetLettersContentResponse, GetLettersResponse } from '@shared/api';
import { DeleteLetterModal, Letter, LetterMenu, Tab, TableHeader } from './widgets';
import { LetterPopUp } from '@widgets';
import { Preloader, PageLayout, Button } from '@shared/widgets';
import LettersIcon from '@assets/images/letters_mobile.svg';

export interface SearchType {
  subject?: string;
  time?: string;
  date?: string;
}

export const LettersPage = () => {
  const lettersStore = useLettersStore();
  const lettersCountStore = useLettersCountStore();
  const deleteLettersStore = useDeleteLetterStore();
  const lettersContentStore = useLetterContentStore();
  const lettersToArchiveStore = useArchiveLetterStore();
  const lettersViewStore = useViewLetterStore();

  const [showLetterPopUp, setShowLetterPopUp] = useState<boolean>(false);

  const [lettersSource, setLetters] = useState(lettersStore.data);
  const [letterFilter, setLetterFilter] = useState<string>('');
  const [modalDelete, setModalDelete] = useState<boolean>(false);
  const [deletedId, setDeletedId] = useState<string>('');
  const [openLetter, setOpenLetter] = useState<GetLettersResponse | null>(null);
  const [tabs, setTabs] = useState<GetLettersResponse[]>([]);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<SearchType | null>(null);
  const refCompose = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!filterOpen) setSearch({});
  }, [filterOpen, setSearch]);

  const searchFilter = useDebounce(search);

  useEffect(() => {
    lettersStore.getLetters();
  }, []);

  useEffect(() => {
    if (lettersStore.data) {
      if (letterFilter) {
        setSearch(null);
        switch (letterFilter) {
          case 'important':
            setLetters(lettersStore.data.filter((i) => i[letterFilter] && !i.deleted && !i.archive));
            break;
          case 'archive':
            setLetters(lettersStore.data.filter((i) => i[letterFilter] && !i.deleted));
            break;
          case 'outgoing':
            setLetters(lettersStore.data.filter((i) => i[letterFilter] && !i.archive));
            break;
        }
      } else {
        setLetters(lettersStore.data.filter((i) => !i.deleted && !i.archive));
      }
    }
  }, [letterFilter, lettersStore.data, setSearch]);

  useEffect(() => {
    if (lettersToArchiveStore.loading || deleteLettersStore.loading) {
      lettersStore.clear();
      lettersStore.getLetters();
    }
  }, [lettersToArchiveStore.loading, deleteLettersStore.loading]);

  const readContentHandler = async (letter: GetLettersResponse) => {
    if (letter?.type === 'Message') await lettersContentStore.getLetterContent(letter.id);
    else if (letter) await lettersViewStore.viewLetter(letter.id);
    if (!letter.read) lettersCountStore.getLettersCount();
    setShowLetterPopUp(true);
    setOpenLetter(letter);
  };
  const importantHandler = (item: GetLettersResponse) => {
    if (!letters) return;
    const i = letters!.indexOf(item);
    if (i >= 0) {
      const newLetters = [...letters];
      newLetters[i] = { ...item };
      newLetters[i].important = !newLetters[i].important;
      setLetters(newLetters);
    }
  };
  const archiveHandler = (item: GetLettersResponse) => lettersToArchiveStore.archiveLetter(item.id);
  const deleteHandler = (item: GetLettersResponse | GetLettersContentResponse) => {
    setDeletedId(item.id);
    setModalDelete(true);
  };

  const minimizeLetter = (letter: GetLettersResponse) => setTabs((prev) => [...prev, letter]);
  const deleteTab = (index: number) => {
    const newTabs = [...tabs];
    newTabs.splice(index, 1);
    setTabs(newTabs);
  };
  const handleOpenTab = (id: string, e: MouseEvent<HTMLDivElement>, tab: GetLettersResponse, key: number) => {
    deleteTab(key);
    if (!id) {
      if (refCompose.current) refCompose.current.click();
    } else readContentHandler(tab);
  };

  const letters = useMemo(() => {
    if (!lettersSource) return lettersSource;
    if (!searchFilter || Object.keys(searchFilter).length === 0) return lettersSource;
    return lettersSource.filter((item) => {
      if (searchFilter) {
        if (searchFilter.subject) {
          if (!(item.subject || '').match(new RegExp(searchFilter.subject, 'i'))) return false;
        }
        if (searchFilter.date) {
          const value = dayjs(item.date).format('DD MMM YYYY');
          if (!value.match(new RegExp(searchFilter.date, 'i'))) return false;
        }
        if (searchFilter.time) {
          const value = dayjs(item.date).format('h:mm a');
          if (!value.match(new RegExp(searchFilter.time, 'i'))) return false;
        }
      }
      return true;
    });
  }, [lettersSource, searchFilter]);
  return (
    <PageLayout>
      <LettersIcon className={cn('mx-auto mt-4 mb-3 hidden', 'max-md:inherit')} />
      {lettersStore.data && letters ? (
        <div className={cn('relative mt-10 flex pb-10', 'max-md:m-0 max-md:block max-md:p-4')} style={{ minHeight: 'calc(100vh - 275px - 50px)' }}>
          <div className={cn('h-25 shrink-0 basis-[11.7%]', 'max-md:mb-12 max-md:h-auto')}>
            <div
              className={cn(
                'pl-23.75',
                'max-md:mb-8 max-md:flex max-md:items-center max-md:p-0',
                'max-md:before:w-1/2 max-md:before:font-[ProximaNova-Medium] max-md:before:text-[28px] max-md:before:content-["Letters"]',
              )}
            >
              <LetterPopUp
                isOpen={showLetterPopUp}
                onClose={() => {
                  setShowLetterPopUp(false);
                  setOpenLetter(null);
                }}
                data={openLetter}
                onMinimizeLetter={minimizeLetter}
                onDelete={deleteHandler}
                onReply={() => setOpenLetter(null)}
                onSubmit={() => lettersStore.getLetters()}
              />
              <Button className='max-md:w-1/2 max-md:pl-5' fullWidth onClick={() => setShowLetterPopUp(true)}>
                + Compose
              </Button>
            </div>
            <LetterMenu filter={letterFilter} onFilter={(filter) => setLetterFilter(filter)} />
          </div>
          <div className={cn('mb-5 ml-8 shrink-0 basis-[73%] overflow-hidden', 'max-md:m-0')}>
            <table
              className={cn(
                'mr-23.75 w-full border-separate border-spacing-x-0 border-spacing-y-0.5 text-center',
                'max-md:block',
                letters.length === 0 ? 'h-62.5' : 'h-auto',
              )}
            >
              <thead className='max-md:hidden'>
                <tr className='my-px min-h-16.5 bg-[#272727]'>
                  <td className='h-16.5 w-[10%]' />
                  <td className='border-spacing-0 border-none p-0 text-center font-medium text-[#e5c496]'>
                    <TableHeader
                      open={filterOpen}
                      onToggle={() => setFilterOpen((prev) => !prev)}
                      value={search?.date || ''}
                      onChange={(value) => setSearch((prev) => ({ ...prev, date: value }))}
                    >
                      Date
                    </TableHeader>
                  </td>
                  <td className='border-spacing-0 border-none p-0 text-center font-medium text-[#e5c496]'>
                    <TableHeader
                      open={filterOpen}
                      onToggle={() => setFilterOpen((prev) => !prev)}
                      value={search?.subject || ''}
                      onChange={(value) => setSearch((prev) => ({ ...prev, subject: value }))}
                    >
                      Subject
                    </TableHeader>
                  </td>
                  <td className='border-spacing-0 border-none p-0 text-center font-medium text-[#e5c496]'>
                    <TableHeader
                      open={filterOpen}
                      onToggle={() => setFilterOpen((prev) => !prev)}
                      value={search?.time || ''}
                      onChange={(value) => setSearch((prev) => ({ ...prev, time: value }))}
                    >
                      Time
                    </TableHeader>
                  </td>
                  <td className='w-[1%]' />
                </tr>
              </thead>
              <tbody className='max-md:block max-md:w-full'>
                {!letters.length && (
                  <tr
                    className={cn(
                      'my-px h-16.5 w-full bg-[#e6e6e6] text-center',
                      'max-md:relative max-md:mb-4 max-md:block max-md:h-auto max-md:rounded-lg max-md:bg-white max-md:p-4',
                    )}
                  >
                    <td className={cn('h-16.5 text-center font-medium text-[#4a4a4a]', 'max-md:h-45 max-md:leading-45')} colSpan={5}>
                      Letters were not found
                    </td>
                  </tr>
                )}
                {letters.map((letter, idx) => (
                  <Letter
                    key={letter.id}
                    idx={idx}
                    letter={letter}
                    onReadContent={readContentHandler}
                    onImportant={importantHandler}
                    onArchive={archiveHandler}
                    onDelete={deleteHandler}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <DeleteLetterModal letterId={deletedId} isOpen={modalDelete} onClose={() => setModalDelete(false)} />
          <div className={cn('absolute right-0 bottom-0 z-5 flex', 'max-md:fixed max-md:right-4 max-md:left-4')}>
            {tabs.map((tab, idx) => (
              <Tab key={idx} tab={tab} idx={idx} onOpenTab={handleOpenTab} onDeleteTab={deleteTab} />
            ))}
          </div>
        </div>
      ) : (
        <Preloader />
      )}
    </PageLayout>
  );
};
